'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import { SchedulingModal } from './google-calendar-button';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';
import { UserAccountDropdown } from '~/(tenant)/_components/user-account-dropdown';
import type { JWTUserData } from '@kit/supabase/types';
import { useSession, useUserRole, useTeamAccount } from '~/lib/hooks/use-header-auth';

const ModeToggle = dynamic(() =>
  import('@kit/ui/mode-toggle').then((mod) => ({
    default: mod.ModeToggle,
  }))
);

const MobileModeToggle = dynamic(() =>
  import('@kit/ui/mobile-mode-toggle').then((mod) => ({
    default: mod.MobileModeToggle,
  }))
);

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/#demo',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

function ScheduleDemoButton({
  isSolid,
  onNavigate,
}: {
  isSolid: boolean;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => {
          onNavigate?.();
          setOpen(true);
        }}
        className={cn(
          'inline-flex items-center justify-center font-roboto-mono text-sm h-10 px-6 rounded-full transition-colors duration-200 whitespace-nowrap',
          isSolid
            ? 'bg-[#202020] text-white hover:bg-[#202020]/90'
            : 'bg-white text-[#202020] hover:bg-white/90'
        )}
      >
        <Trans i18nKey={'common:scheduleDemo'} defaults="Schedule Demo" />
      </button>
      <SchedulingModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

interface MarketingHeaderAccountSectionProps {
  onNavigate?: () => void;
  isSolid?: boolean;
}

export function MarketingHeaderAccountSection({
  onNavigate,
  isSolid = false,
}: MarketingHeaderAccountSectionProps) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  if (session.data) {
    const role = userRole.data || 'guest';

    if (role === 'owner' || role === 'member') {
      return (
        <div
          className={'animate-in fade-in flex items-center gap-x-4 duration-500'}
        >
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <ScheduleDemoButton isSolid={isSolid} onNavigate={onNavigate} />

          <Link
            href={
              teamAccount.data
                ? `/home/${teamAccount.data.slug}/dashboard`
                : '/home/settings'
            }
            onClick={onNavigate}
            className={cn(
              'hidden md:inline-flex items-center justify-center border-2 font-roboto-mono text-sm h-10 px-6 rounded-full transition-colors duration-200 whitespace-nowrap',
              isSolid
                ? 'border-[#202020] text-[#202020] hover:bg-[#202020]/10'
                : 'border-white text-white hover:bg-white/10'
            )}
          >
            <Trans i18nKey={'common:adminPanel'} defaults="Admin Panel" />
          </Link>

          <PersonalAccountDropdown
            showProfileName={false}
            paths={paths}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }

    if (role === 'user') {
      return (
        <div
          className={'animate-in fade-in flex items-center gap-x-4 duration-500'}
        >
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <ScheduleDemoButton isSolid={isSolid} onNavigate={onNavigate} />

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }
  }

  return <AuthButtons onNavigate={onNavigate} isSolid={isSolid} />;
}

function AuthButtons({
  onNavigate,
  isSolid,
}: {
  onNavigate?: () => void;
  isSolid?: boolean;
}) {
  return (
    <div className={'animate-in fade-in flex items-center gap-x-4 duration-500'}>
      <div className={'hidden md:flex'}>
        <If condition={features.enableThemeToggle}>
          <ModeToggle />
        </If>
      </div>

      <div className={'md:hidden'}>
        <If condition={features.enableThemeToggle}>
          <MobileModeToggle />
        </If>
      </div>

      <ScheduleDemoButton isSolid={isSolid ?? false} onNavigate={onNavigate} />

      <a
        href={pathsConfig.auth.signIn}
        onClick={onNavigate}
        className={cn(
          'inline-flex items-center justify-center border-2 font-roboto-mono text-sm h-10 px-6 rounded-full transition-colors duration-200 whitespace-nowrap',
          isSolid
            ? 'border-[#202020] text-[#202020] hover:bg-[#202020]/10'
            : 'border-white text-white hover:bg-white/10'
        )}
      >
        <Trans i18nKey={'auth:signIn'} defaults="Sign In" />
      </a>
    </div>
  );
}
